import { useCallback, useEffect, useRef } from "react";
import {
  hasDirectiveInvocationNodes,
  insertDirectiveInvocationAtTrailingSlash,
  setEditorPlainText,
} from "duck/ui/chat/components/DirectiveComposer.editorContent";
import {
  getSlashQueryFromCurrentRead,
  getTrailingSlashQueryMatch,
} from "duck/ui/chat/components/DirectiveComposer.querying";
import {
  $createParagraphNode,
  $createTextNode,
  $getRoot,
  $getSelection,
  $isRangeSelection,
  $isTextNode,
  COMMAND_PRIORITY_HIGH,
  INSERT_LINE_BREAK_COMMAND,
  KEY_DOWN_COMMAND,
  KEY_ENTER_COMMAND,
  SKIP_DOM_SELECTION_TAG,
  SKIP_SCROLL_INTO_VIEW_TAG,
  type EditorState,
  type LexicalEditor,
  type NodeKey,
} from "lexical";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { OnChangePlugin } from "@lexical/react/LexicalOnChangePlugin";

import type {
  DirectiveComposerState,
  DirectiveComposerSuggestion,
} from "./DirectiveComposer.types";
import {
  $createDirectiveInvocationNode,
  $isDirectiveInvocationNode,
} from "./DirectiveComposerInvocationNode";

interface DirectiveComposerPluginsProps {
  activeSuggestionIndex: number;
  disabled: boolean;
  inputRef: React.RefObject<HTMLElement | null>;
  matchingDirectives: DirectiveComposerSuggestion[];
  value: string;
  onChange: (state: DirectiveComposerState) => void;
  onSlashQueryChange: (query: string | null) => void;
  onSubmit: () => void;
  onSuggestionArrowKey: (key: string) => boolean;
}

// Maps the chat composer's Lexical root element back to its editor so test
// helpers (and any DOM-first code) can find the editor owning an element.
const directiveComposerEditors = new WeakMap<HTMLElement, LexicalEditor>();

export const DirectiveComposerPlugins = ({
  activeSuggestionIndex,
  disabled,
  inputRef,
  matchingDirectives,
  value,
  onChange,
  onSlashQueryChange,
  onSubmit,
  onSuggestionArrowKey,
}: DirectiveComposerPluginsProps) => {
  const [editor] = useLexicalComposerContext();
  const lastEmittedValueRef = useRef(value);

  useEffect(
    () =>
      editor.registerRootListener((rootElement, previousRootElement) => {
        inputRef.current = rootElement;

        if (previousRootElement) {
          directiveComposerEditors.delete(previousRootElement);
        }

        if (rootElement) {
          directiveComposerEditors.set(rootElement, editor);
          rootElement.focus();
        }
      }),
    [editor, inputRef]
  );

  useEffect(() => {
    editor.setEditable(!disabled);
  }, [disabled, editor]);

  useEffect(() => {
    const snapshot = getDirectiveComposerState(editor);
    if (snapshot.text === value) return;

    // Ignore echoes of what the editor itself just emitted; otherwise we would
    // clobber pills (and the caret) on every ordinary parent re-render.
    if (lastEmittedValueRef.current === value) return;

    // External writers (speech-to-text transcripts, post-send clearing)
    // replace the whole content as plain text; directive pills are not
    // preserved across external pushes by design.
    setEditorPlainText(editor, value, { skipDomSelection: true });
  }, [editor, value]);

  useEffect(() => {
    editor.focus();
  }, [editor]);

  useEffect(
    () =>
      editor.registerCommand(
        KEY_ENTER_COMMAND,
        (event) => {
          if (!event) return false;

          if (event.ctrlKey || event.metaKey) {
            event.preventDefault();
            editor.dispatchCommand(INSERT_LINE_BREAK_COMMAND, false);

            return true;
          }

          if (event.shiftKey || disabled) return false;

          if (matchingDirectives.length > 0) {
            const directive = matchingDirectives[activeSuggestionIndex];
            if (directive) {
              event.preventDefault();
              selectDirectiveInvocation(editor, directive);

              return true;
            }
          }

          event.preventDefault();
          onSubmit();

          return true;
        },
        COMMAND_PRIORITY_HIGH
      ),
    [activeSuggestionIndex, disabled, editor, matchingDirectives, onSubmit]
  );

  useEffect(
    () =>
      editor.registerCommand(
        KEY_DOWN_COMMAND,
        (event) => {
          onSlashQueryChange(getSlashQueryFromCurrentRead());

          if (disabled || matchingDirectives.length === 0) return false;

          if (onSuggestionArrowKey(event.key)) {
            event.preventDefault();

            return true;
          }

          if (event.key === "Tab" && !event.shiftKey) {
            const directive = matchingDirectives[activeSuggestionIndex];
            if (!directive) return false;

            event.preventDefault();
            selectDirectiveInvocation(editor, directive);

            return true;
          }

          return false;
        },
        COMMAND_PRIORITY_HIGH
      ),
    [
      activeSuggestionIndex,
      disabled,
      editor,
      matchingDirectives,
      onSlashQueryChange,
      onSuggestionArrowKey,
    ]
  );

  const handleChange = useCallback(
    (editorState: EditorState) => {
      editorState.read(() => {
        const state = getDirectiveComposerStateFromCurrentRead();
        lastEmittedValueRef.current = state.text;
        onChange(state);
        onSlashQueryChange(getSlashQueryFromCurrentRead());
      });
    },
    [onChange, onSlashQueryChange]
  );

  return <OnChangePlugin onChange={handleChange} />;
};

export const getDirectiveComposerEditorFromElement = (
  element: HTMLElement
): LexicalEditor | null => directiveComposerEditors.get(element) ?? null;

export const setDirectiveComposerPlainTextForTesting = (
  editor: LexicalEditor,
  text: string
) => {
  setEditorPlainText(editor, text, { skipDomSelection: true });
};

export const insertDirectiveInvocationForTesting = (
  editor: LexicalEditor,
  directive: DirectiveComposerSuggestion
) => {
  editor.update(
    () => {
      insertDirectiveInvocationAtSelection(directive);
    },
    {
      discrete: true,
      tag: SKIP_DOM_SELECTION_TAG,
    }
  );
};

export const selectDirectiveInvocation = (
  editor: LexicalEditor,
  directive: DirectiveComposerSuggestion
) => {
  editor.update(
    () => {
      insertDirectiveInvocationAtSelection(directive);
    },
    {
      discrete: true,
      tag: SKIP_SCROLL_INTO_VIEW_TAG,
    }
  );

  editor.focus();
};

const insertDirectiveInvocationAtSelection = (
  directive: DirectiveComposerSuggestion
): NodeKey => {
  const directiveNode = $createDirectiveInvocationNode(directive);
  const directiveNodeKey = directiveNode.getKey();
  const trailingSpace = $createTextNode(" ");
  const selection = $getSelection();

  if (!$isRangeSelection(selection)) {
    if (
      insertDirectiveInvocationAtTrailingSlash(directiveNode, trailingSpace)
    ) {
      return directiveNodeKey;
    }

    const paragraph = $createParagraphNode();
    paragraph.append(directiveNode, trailingSpace);
    $getRoot().append(paragraph);
    trailingSpace.select(1, 1);

    return directiveNodeKey;
  }

  const anchorNode = selection.anchor.getNode();

  if ($isTextNode(anchorNode)) {
    const offset = selection.anchor.offset;
    const text = anchorNode.getTextContent();
    const textBeforeSelection = text.slice(0, offset);
    const slashMatch = getTrailingSlashQueryMatch(textBeforeSelection);

    if (slashMatch) {
      const slashStart = offset - slashMatch[0].length;
      const beforeSlash = text.slice(0, slashStart);
      const afterSelection = text.slice(offset);
      anchorNode.setTextContent(beforeSlash);
      const insertedDirective = anchorNode.insertAfter(directiveNode);
      const nextTextNode = $createTextNode(
        `${afterSelection ? afterSelection : ""} `
      );
      insertedDirective.insertAfter(nextTextNode);
      nextTextNode.select(
        nextTextNode.getTextContentSize(),
        nextTextNode.getTextContentSize()
      );

      return directiveNodeKey;
    }
  }

  if (insertDirectiveInvocationAtTrailingSlash(directiveNode, trailingSpace)) {
    return directiveNodeKey;
  }

  selection.insertNodes([directiveNode, trailingSpace]);
  trailingSpace.select(1, 1);

  return directiveNodeKey;
};

const getDirectiveComposerState = (
  editor: LexicalEditor
): DirectiveComposerState =>
  editor.getEditorState().read(getDirectiveComposerStateFromCurrentRead);

const getDirectiveComposerStateFromCurrentRead = (): DirectiveComposerState => {
  const root = $getRoot();
  const text = root.getTextContent();

  return {
    hasContent:
      Boolean(text.trim()) ||
      hasDirectiveInvocationNodes(root, $isDirectiveInvocationNode),
    text,
  };
};
